import React, { useState, useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useFormikContext, Field } from 'formik';
import { Input, Tooltip } from 'syngenta-digital-cropwise-react-ui-kit';
import InfoIcon from 'assets/info.svg?react';
import { DEFAULT_ROUND, Maths } from 'helpers/maths';
import formatNumber from 'helpers/formatNumber';
import Decimal from 'decimal.js';

export const DEFAULT_BLUR_TIMEOUT = 300;

// Grouped by slicing rather than a lookahead. The previous
// /(\d)(?=(\d{3})+(?!\d))/ nests a quantifier inside a lookahead, which
// backtracks super-linearly on long digit runs. Non-digit input is returned
// untouched, matching what the regex did by simply not matching.
function groupThousands(digits) {
  if (!/^\d+$/.test(digits)) {
    return digits;
  }
  // Built left to right so nothing has to be reversed: only the leading group
  // is short, every group after it is exactly three digits.
  const leadingLength = digits.length % 3 || 3;
  const groups = [digits.slice(0, leadingLength)];
  for (let i = leadingLength; i < digits.length; i += 3) {
    groups.push(digits.slice(i, i + 3));
  }
  return groups.join(',');
}

function addThousandsSeparators(num) {
  const numStr = num?.toString() ?? '';

  // Handle negative numbers
  const isNegative = numStr.startsWith('-');
  const absoluteValue = isNegative ? numStr.substring(1) : numStr;

  let formatted;
  if (absoluteValue.includes('.')) {
    const [integerPart, decimalPart] = absoluteValue.split('.');
    formatted = `${groupThousands(integerPart)}.${decimalPart ?? ''}`;
  } else {
    formatted = groupThousands(absoluteValue);
  }

  return isNegative ? `-${formatted}` : formatted;
}

const FormInputWithPrecisionDisplay = ({
  object = /** @type {object|null} */ (null),
  fieldName,
  type = 'text',
  isRequired = false,
  precision = 2,
  maxPrecision = /** @type {number|undefined} */ (undefined),
  inputSuffix = /** @type {React.ReactNode} */ (null),
  id = '',
  inputPrefix = /** @type {React.ReactNode} */ (''),
  onControlledChange = /** @type {((...args: any[]) => any)|null} */ (null),
  disabled = false,
  round = DEFAULT_ROUND,
  onControlledBlur = /** @type {((...args: any[]) => any)|null} */ (null),
  label = '',
  tooltip = '',
  tooltipPlacement = 'top',
  tooltipAlign = /** @type {object|undefined} */ (undefined),
  className = '',
  addonAfter = /** @type {React.ReactNode} */ (null),
  status = '',
  allowEmptyDisplay = false,
  allowNegative = false
}) => {
  let {
    values,
    touched,
    errors,
    setFieldValue,
    setFieldTouched,
    validateField
  } = { values: [], touched: [], errors: [] };
  const formikContext = useFormikContext();
  if (!object && formikContext) {
    values = formikContext.values;
    touched = formikContext.touched;
    errors = formikContext.errors;
    setFieldValue = formikContext.setFieldValue;
    setFieldTouched = formikContext.setFieldTouched;
    validateField = formikContext.validateField;
  }

  const error = errors[fieldName];
  const [fieldValueOnFocus, setFieldValueOnFocus] = useState(0);
  const [displayValue, setDisplayValue] = useState('');
  const [isEditing, setIsEditing] = useState(false);

  const sanitizeValue = useCallback(
    value =>
      value?.toString()?.replaceAll(allowNegative ? /[^\d.-]/g : /[^\d.]/g, ''),
    [allowNegative]
  );

  const formatWithPrecision = useCallback(
    (value, precisionValue) => {
      const roundedValue = Maths.parseFloatRound(
        sanitizeValue(value),
        precisionValue,
        Decimal.ROUND_HALF_UP
      )?.toFixed(precisionValue);
      const isNonValidValue = Number.isNaN(roundedValue);
      const valueRounded = isNonValidValue ? value : roundedValue;
      return formatNumber(
        valueRounded,
        '',
        precisionValue,
        Decimal.ROUND_HALF_UP
      );
    },
    [sanitizeValue]
  );

  const formatWithMaxPrecision = useCallback(
    (value, maxPrecisionValue) => {
      const roundedValue = Maths.parseFloatRound(
        sanitizeValue(value),
        maxPrecisionValue,
        Decimal.ROUND_HALF_UP
      );
      const isNonValidValue = Number.isNaN(roundedValue);
      const valueRounded = isNonValidValue ? value : roundedValue;

      if (valueRounded == null || valueRounded === '') return '';

      const numValue = Number.parseFloat(valueRounded);
      if (Number.isNaN(numValue)) return valueRounded;

      const formatter = new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: maxPrecisionValue
      });
      return formatter.format(numValue);
    },
    [sanitizeValue]
  );

  const updateDisplayValue = useCallback(
    value => {
      if (error) {
        setDisplayValue(value);
        return;
      }

      if ((value == null || value === '') && allowEmptyDisplay) {
        setDisplayValue('');
        return;
      }

      const formattedValue =
        maxPrecision === undefined
          ? formatWithPrecision(value, precision)
          : formatWithMaxPrecision(value, maxPrecision);

      setDisplayValue(formattedValue);
    },
    [
      precision,
      maxPrecision,
      error,
      allowEmptyDisplay,
      formatWithPrecision,
      formatWithMaxPrecision
    ]
  );

  useEffect(() => {
    const value = object ? object[fieldName] : values[fieldName];
    setFieldValueOnFocus(addThousandsSeparators(value));
  }, [object, values, fieldName, updateDisplayValue, isEditing]);

  useEffect(() => {
    if (!isEditing) {
      updateDisplayValue(fieldValueOnFocus);
    }
  }, [isEditing, fieldValueOnFocus, updateDisplayValue]);

  const handleChange = field => event => {
    const { value, selectionStart } = event.target;
    const element = event.target;
    const sanitizedValue = allowNegative
      ? value
          .replaceAll(/[^0-9.-]/g, '') // Allow digits, dots, and minus sign
          .replaceAll(/(?!^)-/g, '') // Remove minus signs that are not at the start
          .replaceAll(/\.(?=.*\.)/g, '') // Remove duplicate dots
      : value
          .replaceAll(/[^0-9.]/g, '') // Only allow digits and dots
          .replaceAll(/\.(?=.*\.)/g, ''); // Remove duplicate dots
    if (object) {
      // eslint-disable-next-line no-param-reassign
      object[field] = sanitizedValue;
      updateDisplayValue(sanitizedValue);
    } else {
      setFieldTouched(fieldName, true);
      setFieldValue(field, sanitizedValue).then(() => {
        validateField(field);
        updateDisplayValue(sanitizedValue);
      });
    }
    const formattedValue = addThousandsSeparators(sanitizedValue);

    setFieldValueOnFocus(formattedValue);
    window.requestAnimationFrame(() => {
      // compare the formatted value with the value in the input in amount of commas
      // if the formatted value has more commas than the value in the input
      // it means that the user has typed a comma
      // and we should move the cursor to the right
      // if it is equal, it means that we need to keep the cursor in the same position
      // otherwise, we should move the cursor to the left
      const commasInFormattedValue = (formattedValue.match(/,/g) || []).length;
      const commasInValue = (value.match(/,/g) || []).length;
      let newSelectionStart;
      if (commasInFormattedValue > commasInValue) {
        newSelectionStart = selectionStart + 1;
      } else if (commasInFormattedValue < commasInValue) {
        newSelectionStart = selectionStart - 1;
      } else {
        newSelectionStart = selectionStart;
      }
      element.selectionStart = newSelectionStart;
      element.selectionEnd = newSelectionStart;
    });

    if (onControlledChange) onControlledChange(event);
  };

  // The pending blur timer has to be cancelled on unmount, otherwise it fires
  // against a component that is gone — which in tests means setting state after
  // the DOM has been torn down.
  const blurTimeoutRef = useRef();
  useEffect(() => () => clearTimeout(blurTimeoutRef.current), []);

  const handleBlur = () => {
    clearTimeout(blurTimeoutRef.current);
    blurTimeoutRef.current = setTimeout(() => {
      setIsEditing(false);
      if (!errors[fieldName]) {
        const updatedValue = Maths.parseFloatRound(
          fieldValueOnFocus
            ?.toString()
            ?.replaceAll(allowNegative ? /[^\d.-]/g : /[^\d.]/g, ''),
          round,
          Decimal.ROUND_HALF_UP
        );
        const updatedValueSanitized = Number.isNaN(updatedValue)
          ? ''
          : updatedValue;
        if (object) {
          // eslint-disable-next-line no-param-reassign
          object[fieldName] = updatedValueSanitized;
          updateDisplayValue(fieldValueOnFocus);
        } else {
          setFieldValue(fieldName, updatedValueSanitized).then(() => {
            validateField(fieldName);
            updateDisplayValue(fieldValueOnFocus);
          });
          // if there are errors with the field,
          // we should not set it as untouched,
          // otherswise the error message
          // will not be displayed
          setFieldTouched(fieldName, false);
        }
        if (onControlledBlur) onControlledBlur(updatedValueSanitized);
      }
      if (onControlledBlur) onControlledBlur(fieldValueOnFocus);
    }, DEFAULT_BLUR_TIMEOUT);
  };

  const handleFocus = () => {
    setIsEditing(true);
  };

  const innerInput = (
    <div className="relative w-full">
      {tooltip && (
        <div className="syt-antd-input-label">
          <label>
            <span className="inline-flex items-center">
              {label}
              <Tooltip
                title={tooltip}
                placement={tooltipPlacement}
                color="#232630"
                autoAdjustOverflow={false}
                align={tooltipAlign}
              >
                <span
                  className="inline-flex items-center cursor-default ml-1"
                  style={{ width: 16, height: 16, flexShrink: 0 }}
                >
                  <InfoIcon className="w-16px h-16px" />
                </span>
              </Tooltip>
            </span>
          </label>
        </div>
      )}
      <Input
        data-testid={`${fieldName}-input`}
        id={id}
        disabled={disabled}
        prefix={inputPrefix}
        suffix={inputSuffix}
        size="middle"
        type={type}
        required={isRequired}
        status={status}
        className={`${className} ${addonAfter ? 'cwf-grouped-input' : ''} ${
          errors[fieldName] && touched[fieldName] ? 'border border-remove' : ''
        }`}
        value={isEditing ? fieldValueOnFocus : displayValue}
        onChange={handleChange(fieldName)}
        onBlur={handleBlur}
        onFocus={handleFocus}
        label={tooltip ? '' : label}
        addonAfter={addonAfter}
      />
    </div>
  );

  return object ? (
    innerInput
  ) : (
    <Field name={fieldName}>{() => innerInput}</Field>
  );
};

FormInputWithPrecisionDisplay.propTypes = {
  fieldName: PropTypes.string.isRequired,
  isRequired: PropTypes.bool,
  precision: PropTypes.number,
  maxPrecision: PropTypes.number,
  inputSuffix: PropTypes.node,
  inputPrefix: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  id: PropTypes.string,
  type: PropTypes.string,
  onControlledChange: PropTypes.func,
  round: PropTypes.number,
  // eslint-disable-next-line react/forbid-prop-types
  object: PropTypes.object,
  disabled: PropTypes.bool,
  onControlledBlur: PropTypes.func,
  label: PropTypes.node,
  tooltip: PropTypes.string,
  tooltipPlacement: PropTypes.string,
  // eslint-disable-next-line react/forbid-prop-types
  tooltipAlign: PropTypes.object,
  className: PropTypes.string,
  addonAfter: PropTypes.node,
  status: PropTypes.string,
  allowEmptyDisplay: PropTypes.bool,
  allowNegative: PropTypes.bool
};

export default FormInputWithPrecisionDisplay;
